import { useEffect } from "react";
import type { BrowseTrack } from "@touchscreen/api-client";
import { useTouchscreenContext } from "@touchscreen/hooks";
import { ConfirmRequestSheet } from "./ConfirmRequestSheet.js";
import { useRequestFlow } from "../lib/useRequestFlow.js";
import { addMyRequest } from "../lib/myRequests.js";
import { cacheTrack } from "../lib/trackCache.js";
import "./RequestSheetHost.css";

export interface RequestSheetHostProps {
  track: BrowseTrack;
  onClose: () => void;
}

/**
 * Owns one request attempt end to end: geofence position (if required), submit, and the
 * confirmation — including the real `queue_position`. Legacy said "it will be played shortly";
 * there is no estimated wait time in this product, so we show the actual position instead.
 */
export function RequestSheetHost(props: RequestSheetHostProps): React.JSX.Element {
  const { flow, submit, reset, isCoolingDown } = useRequestFlow();
  const { resetVenue } = useTouchscreenContext();

  useEffect(() => {
    if (flow.status === "success" || flow.status === "duplicate") {
      cacheTrack(props.track.sm_id, { title: props.track.title, artist: props.track.artist });
      addMyRequest({
        requestId: flow.result.request_id,
        smId: props.track.sm_id,
        queuePosition: flow.result.queue_position,
      });
      const timer = setTimeout(() => {
        reset();
        props.onClose();
      }, 2200);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.status]);

  if (flow.status === "success" || flow.status === "duplicate") {
    return (
      <div className="request-confirmation" role="status">
        <p className="request-confirmation__title">You're on the list</p>
        <p className="request-confirmation__detail">Queue position: {flow.result.queue_position}</p>
      </div>
    );
  }

  const disabledReason = isCoolingDown(props.track.sm_id)
    ? "You just requested this track"
    : flow.status === "checking_geofence"
      ? "Checking you're at this venue…"
      : null;
  const errorMessage =
    flow.status === "error"
      ? flow.message
      : flow.status === "share_location"
        ? "You'll need to share your location to request here"
        : null;
  // Geofence presence hasn't been proven — whether that's a denied/unsupported permission or a
  // granted position outside range, neither fixes itself by tapping Request again. Swap the
  // button itself for a way out to the venue picker rather than leaving a dead Request button
  // behind an error message.
  const primaryAction =
    flow.status === "share_location" ? { label: "Share location", onClick: resetVenue } : null;

  return (
    <ConfirmRequestSheet
      track={props.track}
      submitting={flow.status === "loading" || flow.status === "locating"}
      disabledReason={disabledReason}
      errorMessage={errorMessage}
      primaryAction={primaryAction}
      onConfirm={() => submit(props.track.sm_id)}
      onCancel={() => {
        reset();
        props.onClose();
      }}
    />
  );
}
